<template>
  <div class="mb-4">
    <h3 class="text-neutral-400 font-semibold text-base mb-2">
      People with access
    </h3>
    <div class="flex flex-col space-y-2">
      <div class="flex flex-row h-[40px] items-center gap-4">
        <div class="block">
          <Avatar
            class="leading-[normal]"
            rounded
            size="small"
            :color="owner.color"
            :src="owner.avatarUrl || owner.gravatarUrl"
            :alt="owner.shortName"
            :initials="owner.shortName"
          />
        </div>
        <div class="w-full font-semibold text-base leading-5 text-neutral-50">
          {{ owner.fullName }}
          {{ isCurrentUser(owner.id) ? '(you)' : '' }}
        </div>
        <div class="md:min-w-36 hidden sm:block">
          <Select model-value="owner" :disabled="true">
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="owner">Owner</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div
        v-for="user in externalUsers"
        :key="user.id"
        class="flex flex-row h-[40px] items-center gap-4"
      >
        <div class="block">
          <Avatar
            class="leading-[normal]"
            rounded
            size="small"
            :color="user.color"
            :src="user.avatarUrl || user.gravatarUrl"
            :alt="user.shortName"
            :initials="user.shortName"
          />
        </div>
        <div class="w-full font-semibold text-base leading-5 text-neutral-50">
          {{ user.fullName }}
          {{ isCurrentUser(user.id) ? '(you)' : '' }}
        </div>
        <Select
          name="role"
          :model-value="user.role"
          :disabled="!isCurrentUser(user.id) && !hasMembershipsDelete"
          @update:model-value="onChangeAccess($event, user.id)"
        >
          <SelectTrigger class="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="viewer">Viewer</SelectItem>
            <SelectItem
              value="editor"
              :disabled="isCurrentUser(user.id) && user.role === 'viewer'"
            >
              Editor
              <Badge v-if="currentSubscriptionPlan">Billable</Badge>
            </SelectItem>
            <!-- <SelectItem v-if="isOwner(currentUser.id)" value="owner">
              Make Owner
              <Badge v-if="currentSubscriptionPlan">Billable</Badge>
            </SelectItem> -->
            <Protect
              :permissions="[SYS_PERMISSIONS.SYS_MEMBERSHIPS_DELETE]"
              :skip-protect="isCurrentUser(user.id)"
            >
              <SelectItem value="remove">
                {{ isCurrentUser(user.id) ? 'Leave' : 'Remove' }}
              </SelectItem>
            </Protect>
          </SelectContent>
        </Select>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useQueryClient, useMutation } from '@tanstack/vue-query';
import { useProtect } from '@/composables/useProtect.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ui/components/select';
import { Badge } from '@ui/components/badge';
import requests from '@vuex/requests';
import Avatar from '@ui/components/avatar/Avatar.vue';
import Protect from '@/hoc/Protect.vue';
import { SYS_PERMISSIONS } from '@/constants.js';
import { useExternalUsers } from '@/queries/composables.js';
import { queries } from '@/queries/index.js';
import { useRouter } from 'vue-router';

const queryClient = useQueryClient();
const store = useStore();
const openProjectHash = computed(() => store.getters['user/projects/getOpen']);
const router = useRouter();

const currentSubscriptionPlan = computed(
  () => store.getters['workspace/getCurrentSubscriptionPlan']
);

const currentUser = computed(() => {
  const currentUserId = store.getters['user/account/getId'];
  return store.getters['project/users/getParticipant'](currentUserId);
});

const { hasPermission: hasMembershipsDelete } = useProtect({
  permissions: [SYS_PERMISSIONS.SYS_MEMBERSHIPS_DELETE],
});

const owner = computed(() => {
  const ownerId = store.getters['project/getOwnerId'];
  return store.getters['project/users/getParticipant'](ownerId);
});

const isCurrentUser = id => currentUser.value?.id === id;
const isOwner = id => owner.value.id === id;

const { data: externalUsers } = useExternalUsers(openProjectHash.value);

const { mutate: changeAccess } = useMutation({
  mutationFn: async payload => {
    await requests.projects.changeExternalUserAccess(payload);
  },
  onSuccess: async () => {
    await queryClient.invalidateQueries({
      queryKey: queries.project.externalUsers(openProjectHash.value).queryKey,
    });
  },
  onError: e => {
    // TODO: show error
    console.error(e);
  },
});

const { mutate: removeExternalUser } = useMutation({
  mutationFn: async payload => {
    await requests.projects.removeExternalUser(payload);
  },
  onSuccess: async (data, payload) => {
    if (isCurrentUser(payload.id)) {
      // If removed user is current user, redirect to projects page
      await router.push({
        name: 'workspace',
        query: { refreshWorkspace: true },
      });
      await queryClient.invalidateQueries();
    } else {
      await queryClient.invalidateQueries({
        queryKey: queries.project.externalUsers(openProjectHash.value).queryKey,
      });
    }
  },
  onError: e => {
    // TODO: show error
    console.error(e);
  },
});

const onChangeAccess = (value, userId) => {
  if (value === 'remove') {
    removeExternalUser({
      hash: openProjectHash.value,
      id: userId,
    });
    return;
  }
  changeAccess({
    id: userId,
    role: value,
    hash: openProjectHash.value,
  });
};
</script>
